import { LG, MD, MIN_LG, MIN_MD } from '@/src/lib/breakpoints';
import { type HomepageHeroConfig, type HomepageHeroId, type HomepageHeroTheme } from '@/src/lib/homepageHero';
import { heroArtwork, heroArtworkImage } from './homepageHero.styles';

type HomepageHeroProps = {
  hero: HomepageHeroConfig;
};

type Tier = 'desktop' | 'tablet' | 'mobile';
type Format = 'avif' | 'png';

// Hosted in the RP/files.zillowrentals.com repo (synced to S3 by Stella on
// merge); regenerate via `hero:optimize` and PR the output to that repo.
const PROCESSED_BASE = 'https://files.zillowrentals.com/hotpads-web-next/homepage-hero/processed';
const PREFERS_DARK = '(prefers-color-scheme: dark)';
const PREFERS_LIGHT = '(prefers-color-scheme: light)';
// Desktop tier gates at MIN_LG (not MIN_XL) so picture asset selection lines
// up with the page chrome's lg-flip — CLAUDE.md mandates a single mobile/desktop
// boundary at lg (768px) for the homepage.
const MIN_LG_DARK = `${MIN_LG} and ${PREFERS_DARK}`;
const MIN_MD_DARK = `${MIN_MD} and ${PREFERS_DARK}`;

const heroAsset = (city: HomepageHeroId, theme: HomepageHeroTheme, tier: Tier, dpr: '1x' | '2x', format: Format) =>
  `${PROCESSED_BASE}/${city}-${theme}-${tier}-${dpr}.${format}`;

const avifSrcSet = (city: HomepageHeroId, theme: HomepageHeroTheme, tier: Tier) =>
  `${heroAsset(city, theme, tier, '1x', 'avif')} 1x, ${heroAsset(city, theme, tier, '2x', 'avif')} 2x`;

const pngSrc = (city: HomepageHeroId, theme: HomepageHeroTheme, tier: Tier) =>
  heroAsset(city, theme, tier, '1x', 'png');

function MediaQuerySources({ city }: { city: HomepageHeroId }) {
  return (
    <>
      {/* Desktop tier (lg+, ≥768px) — dark variant first */}
      <source media={MIN_LG_DARK} type="image/avif" srcSet={avifSrcSet(city, 'night', 'desktop')} />
      <source media={MIN_LG_DARK} type="image/png" srcSet={pngSrc(city, 'night', 'desktop')} />
      <source media={MIN_LG} type="image/avif" srcSet={avifSrcSet(city, 'day', 'desktop')} />
      <source media={MIN_LG} type="image/png" srcSet={pngSrc(city, 'day', 'desktop')} />

      {/* Tablet tier (md → lg-1, 480–767px) */}
      <source media={MIN_MD_DARK} type="image/avif" srcSet={avifSrcSet(city, 'night', 'tablet')} />
      <source media={MIN_MD_DARK} type="image/png" srcSet={pngSrc(city, 'night', 'tablet')} />
      <source media={MIN_MD} type="image/avif" srcSet={avifSrcSet(city, 'day', 'tablet')} />
      <source media={MIN_MD} type="image/png" srcSet={pngSrc(city, 'day', 'tablet')} />

      {/* Mobile tier (<md, <480px) — full AVIF + PNG for both themes */}
      <source media={PREFERS_DARK} type="image/avif" srcSet={avifSrcSet(city, 'night', 'mobile')} />
      <source media={PREFERS_DARK} type="image/png" srcSet={pngSrc(city, 'night', 'mobile')} />
      <source type="image/avif" srcSet={avifSrcSet(city, 'day', 'mobile')} />
      <source type="image/png" srcSet={pngSrc(city, 'day', 'mobile')} />
    </>
  );
}

type HomepageHeroPreloadProps = {
  heroId: HomepageHeroId;
};

// Mutually exclusive conditions (explicit light/dark + bounded ranges) ensure exactly one fetch fires per viewport+theme.
export function HomepageHeroPreload({ heroId }: HomepageHeroPreloadProps) {
  const maxBelowLg = `(max-width: ${parseFloat(LG) - 0.02}em)`;
  const maxBelowMd = `(max-width: ${parseFloat(MD) - 0.02}em)`;

  return (
    <>
      <link
        rel="preload"
        as="image"
        type="image/avif"
        fetchPriority="high"
        media={`${MIN_LG} and ${PREFERS_DARK}`}
        href={heroAsset(heroId, 'night', 'desktop', '1x', 'avif')}
        imageSrcSet={avifSrcSet(heroId, 'night', 'desktop')}
      />
      <link
        rel="preload"
        as="image"
        type="image/avif"
        fetchPriority="high"
        media={`${MIN_LG} and ${PREFERS_LIGHT}`}
        href={heroAsset(heroId, 'day', 'desktop', '1x', 'avif')}
        imageSrcSet={avifSrcSet(heroId, 'day', 'desktop')}
      />
      <link
        rel="preload"
        as="image"
        type="image/avif"
        fetchPriority="high"
        media={`${MIN_MD} and ${maxBelowLg} and ${PREFERS_DARK}`}
        href={heroAsset(heroId, 'night', 'tablet', '1x', 'avif')}
        imageSrcSet={avifSrcSet(heroId, 'night', 'tablet')}
      />
      <link
        rel="preload"
        as="image"
        type="image/avif"
        fetchPriority="high"
        media={`${MIN_MD} and ${maxBelowLg} and ${PREFERS_LIGHT}`}
        href={heroAsset(heroId, 'day', 'tablet', '1x', 'avif')}
        imageSrcSet={avifSrcSet(heroId, 'day', 'tablet')}
      />
      <link
        rel="preload"
        as="image"
        type="image/avif"
        fetchPriority="high"
        media={`${maxBelowMd} and ${PREFERS_DARK}`}
        href={heroAsset(heroId, 'night', 'mobile', '1x', 'avif')}
        imageSrcSet={avifSrcSet(heroId, 'night', 'mobile')}
      />
      <link
        rel="preload"
        as="image"
        type="image/avif"
        fetchPriority="high"
        media={`${maxBelowMd} and ${PREFERS_LIGHT}`}
        href={heroAsset(heroId, 'day', 'mobile', '1x', 'avif')}
        imageSrcSet={avifSrcSet(heroId, 'day', 'mobile')}
      />
    </>
  );
}

export function HomepageHero({ hero }: HomepageHeroProps) {
  const { id } = hero;

  return (
    <picture className={heroArtwork}>
      <MediaQuerySources city={id} />
      <img
        src={pngSrc(id, 'day', 'mobile')}
        width={480}
        height={480}
        alt=""
        loading="eager"
        fetchPriority="high"
        decoding="async"
        className={heroArtworkImage}
      />
    </picture>
  );
}
